/**
 * Regras puras do progresso simulado (previa).
 *
 * Um tique avanca um lote fixo (fracao do total) e injeta uma falha ou um
 * opt-out em tiques determinados -- nada de sorteio, entao dois navegadores
 * abertos na mesma tela mostram a mesma barra. Quando nao sobra ninguem para
 * receber, o item vira "concluido" e ganha `finishedAt`.
 *
 * Decisoes nao obvias:
 *  1. Leitura, clique e resposta crescem junto com as entregas (fracoes
 *     fixas). PAGAMENTO NAO: pagamento chega em ate 7 dias pelo webhook e so
 *     entao e atribuido ao envio (ADR-001). Mostrar "pagou" enquanto o envio
 *     ainda sai seria inventar receita.
 *  2. A trava de opt-out (`autoPauseOptOutBps`) so avalia depois de 100
 *     envios: com 10 mensagens, um SAIR ja seria 10% e pausaria tudo por
 *     ruido.
 */

import type { Broadcast, EmailCampaign } from './types';

const LOTES_ATE_CONCLUIR = 36;
const TIQUE_DE_FALHA = 6;
const TIQUE_DE_OPTOUT = 11;
const LEITURA_BPS = 6_200;
const CLIQUE_BPS = 1_400;
const RESPOSTA_BPS = 800;
const ABERTURA_EMAIL_BPS = 3_200;
const CLIQUE_EMAIL_BPS = 1_900;
const MINIMO_PARA_TRAVA = 100;

function agoraIso(): string {
  return new Date().toISOString();
}

function fracao(base: number, bps: number): number {
  return Math.round((base * bps) / 10_000);
}

export function envioEmAndamento(envio: Broadcast): boolean {
  return envio.status === 'em_andamento';
}

export function avancarEnvio(envio: Broadcast, tique: number): Partial<Broadcast> {
  const restante = envio.totalContacts - envio.sentCount - envio.failedCount - envio.optOutCount;
  if (restante <= 0) {
    return { status: 'concluida', finishedAt: envio.finishedAt ?? agoraIso() };
  }
  const falha = tique % TIQUE_DE_FALHA === 0 && restante > 1 ? 1 : 0;
  const optOut = tique % TIQUE_DE_OPTOUT === 0 && restante > 2 ? 1 : 0;
  const failedCount = envio.failedCount + falha;
  const optOutCount = envio.optOutCount + optOut;
  const teto = envio.totalContacts - failedCount - optOutCount;
  const passo = Math.max(1, Math.ceil(envio.totalContacts / LOTES_ATE_CONCLUIR));
  const sentCount = Math.min(teto, envio.sentCount + passo);
  const deliveredCount = Math.min(sentCount, envio.deliveredCount + passo);
  const novosEntregues = deliveredCount - envio.deliveredCount;
  const novosLidos = fracao(novosEntregues, LEITURA_BPS);
  const concluiu = sentCount >= teto;

  const trava = envio.autoPauseOptOutBps;
  const estourou = trava !== null && sentCount >= MINIMO_PARA_TRAVA && optOutCount * 10_000 > trava * (sentCount + optOutCount);

  return {
    sentCount,
    failedCount,
    optOutCount,
    deliveredCount,
    readCount: envio.readCount + novosLidos,
    clickedCount: envio.clickedCount + fracao(novosLidos, CLIQUE_BPS),
    repliedCount: envio.repliedCount + fracao(novosEntregues, RESPOSTA_BPS),
    status: concluiu ? 'concluida' : estourou ? 'pausada' : 'em_andamento',
    pausedReason: !concluiu && estourou ? 'optout_alto' : null,
    startedAt: envio.startedAt ?? agoraIso(),
    finishedAt: concluiu ? agoraIso() : null,
  };
}

export function campanhaEnviando(campanha: EmailCampaign): boolean {
  return campanha.status === 'enviando';
}

export function avancarCampanha(campanha: EmailCampaign, tique: number): Partial<EmailCampaign> {
  const restante = campanha.totalRecipients - campanha.acceptedCount - campanha.failedCount;
  if (restante <= 0) {
    return { status: 'concluida' };
  }
  const falha = tique % TIQUE_DE_FALHA === 0 && restante > 1 ? 1 : 0;
  const failedCount = campanha.failedCount + falha;
  const teto = campanha.totalRecipients - failedCount;
  const passo = Math.max(1, Math.ceil(campanha.totalRecipients / LOTES_ATE_CONCLUIR));
  const acceptedCount = Math.min(teto, campanha.acceptedCount + passo);
  const novosAceitos = acceptedCount - campanha.acceptedCount;
  const novasAberturas = fracao(novosAceitos, ABERTURA_EMAIL_BPS);
  return {
    acceptedCount,
    failedCount,
    openedCount: campanha.openedCount + novasAberturas,
    clickedCount: campanha.clickedCount + fracao(novasAberturas, CLIQUE_EMAIL_BPS),
    status: acceptedCount >= teto ? 'concluida' : 'enviando',
  };
}
